import { useState, useEffect } from 'react';
import { Bot, Save, RotateCcw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useAiConfig, useSaveAiConfig, useResetAiConfig } from '@/hooks/use-admin';

const TASK_META: Record<string, { label: string; description: string }> = {
  skillExtract: { label: 'CV Parsing', description: 'Extracts skills from uploaded CVs' },
  bulkMatch: { label: 'Bulk Matching', description: 'Heuristic pre-screening' },
  deepMatch: {
    label: 'AI Match Analysis',
    description: 'Deep analysis of candidate-assignment fit'
  },
  teamFit: { label: 'Team Fit Analysis', description: 'Analyzes team composition impact' },
  pitch: { label: 'Pitch Generation', description: 'Creates professional pitch text' },
  insights: { label: 'Market Insights', description: 'Market analysis and intelligence' },
  summary: { label: 'Summaries', description: 'Generates text summaries' }
};

export function AiConfigCard() {
  const { data: configData, isLoading } = useAiConfig();
  const saveConfig = useSaveAiConfig();
  const resetConfig = useResetAiConfig();

  const [localModels, setLocalModels] = useState<Record<string, string>>({});

  const remoteConfig = configData?.data;

  useEffect(() => {
    if (remoteConfig?.models) {
      setLocalModels({ ...remoteConfig.models });
    }
  }, [remoteConfig]);

  const defaults = remoteConfig?.defaults ?? {};
  const available = remoteConfig?.available ?? [];

  const hasCustom = Object.entries(localModels).some(
    ([task, model]) => defaults[task] !== undefined && model !== defaults[task]
  );

  function handleModelChange(task: string, modelId: string) {
    setLocalModels((prev) => ({ ...prev, [task]: modelId }));
  }

  function handleSave() {
    saveConfig.mutate(localModels);
  }

  function handleReset() {
    resetConfig.mutate(undefined, {
      onSuccess: () => {
        setLocalModels({ ...defaults });
      }
    });
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-sm">
          <Bot className="h-4 w-4" />
          AI Model Configuration
          {hasCustom && (
            <Badge
              variant="outline"
              className="bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400"
            >
              Custom
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading ? (
          <p className="text-sm text-muted-foreground">Loading model config...</p>
        ) : (
          <>
            <p className="text-xs text-muted-foreground">
              Select which model to use for each AI task. Models available from Gemini (Google AI)
              and GLM (z.ai).
            </p>

            <div className="space-y-3">
              {Object.keys(TASK_META).map((task) => {
                const meta = TASK_META[task];
                const currentModel = localModels[task] ?? defaults[task] ?? '';
                const isDefault = currentModel === defaults[task];

                return (
                  <div key={task} className="grid grid-cols-1 gap-1 sm:grid-cols-2 sm:gap-4">
                    <div>
                      <p className="text-sm font-medium">{meta.label}</p>
                      <p className="text-xs text-muted-foreground">{meta.description}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <select
                        value={currentModel}
                        onChange={(e) => handleModelChange(task, e.target.value)}
                        className="flex-1 rounded-md border border-input bg-background px-3 py-1.5 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      >
                        <optgroup label="Gemini (Google AI)">
                          {available
                            .filter((m) => m.provider === 'gemini')
                            .map((m) => (
                              <option key={m.id} value={m.id}>
                                {m.name} — {m.description}
                              </option>
                            ))}
                        </optgroup>
                        <optgroup label="GLM (z.ai)">
                          {available
                            .filter((m) => m.provider === 'zai')
                            .map((m) => (
                              <option key={m.id} value={m.id}>
                                {m.name} — {m.description}
                              </option>
                            ))}
                        </optgroup>
                      </select>
                      {!isDefault && (
                        <Badge
                          variant="outline"
                          className="shrink-0 bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400 text-xs"
                        >
                          Custom
                        </Badge>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={handleSave}
                disabled={saveConfig.isPending}
                className="flex-1"
              >
                <Save className="mr-1.5 h-3.5 w-3.5" />
                {saveConfig.isPending ? 'Saving...' : 'Save Config'}
              </Button>
              {hasCustom && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleReset}
                  disabled={resetConfig.isPending}
                >
                  <RotateCcw className="mr-1.5 h-3.5 w-3.5" />
                  {resetConfig.isPending ? 'Resetting...' : 'Reset to Defaults'}
                </Button>
              )}
            </div>

            {!hasCustom && (
              <p className="text-xs text-muted-foreground">
                Using default models — change any dropdown above to override per task.
              </p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
